import * as ERRORS from 'constants/errors';
import * as RENDER_MODES from 'constants/file_render_modes';
import * as MODALS from 'constants/modal_types';
import * as ACTIONS from 'constants/action_types';
import * as PAGES from 'constants/pages';
import { NO_FILE, PAYWALL } from 'constants/publish';
import * as PUBLISH_TYPES from 'constants/publish_types';
import { batchActions } from 'util/batch-actions';
import { THUMBNAIL_CDN_SIZE_LIMIT_BYTES, WEB_PUBLISH_SIZE_LIMIT_GB } from 'config';
import { doCheckPendingClaims, doResolveClaimIds } from 'redux/actions/claims';
import { lighthouse } from 'redux/actions/search';
import { selectProtectedContentMembershipsForContentClaimId } from 'redux/selectors/memberships';
import { doSaveMembershipRestrictionsForContent, doMembershipContentforStreamClaimId } from 'redux/actions/memberships';
import {
  makeSelectClaimForUri,
  selectMyActiveClaims,
  selectMyClaims,
  selectMyChannelClaims,
  selectMyChannelClaimIds,
  selectReflectingById,
  makeSelectMetadataItemForUri,
} from 'redux/selectors/claims';
import { makeSelectFileRenderModeForUri } from 'redux/selectors/content';
import { selectDefaultChannelClaim, selectShowMatureContent } from 'redux/selectors/settings';
import {
  selectPublishFormValue,
  selectPublishFormValues,
  selectIsStillEditing,
  selectMemberRestrictionStatus,
} from 'redux/selectors/publish';
import { doError } from 'redux/actions/notifications';
import { navigateTo } from 'redux/router';
import analytics from 'analytics';
import { doOpenModal, doSetActiveChannel, doSetIncognito } from 'redux/actions/app';
import { CC_LICENSES, COPYRIGHT, OTHER, NONE, PUBLIC_DOMAIN } from 'constants/licenses';
import { IMG_CDN_PUBLISH_URL } from 'constants/cdn_urls';
import * as THUMBNAIL_STATUSES from 'constants/thumbnail_upload_statuses';
import { sanitizeName, buildURI } from 'util/lbryURI';
import { getVideoBitrate, resolvePublishPayload } from 'util/publish';
import { parsePurchaseTag, parseRentalTag, TO_SECONDS } from 'util/stripe';
import Lbry from 'lbry';
import { X_LBRY_AUTH_TOKEN } from 'constants/token';
// import LbryFirst from 'extras/lbry-first/lbry-first';
import { isClaimNsfw, getChannelIdFromClaim, isStreamPlaceholderClaim } from 'util/claim';
import { MEMBERS_ONLY_CONTENT_TAG, SCHEDULED_TAGS, VISIBILITY_TAGS } from 'constants/tags';
const PUBLISH_PATH_MAP = Object.freeze({
  file: PAGES.UPLOAD,
  post: PAGES.POST,
  livestream: PAGES.LIVESTREAM,
});

function resolveClaimTypeForAnalytics(claim) {
  if (!claim) {
    return 'undefined_claim';
  }

  switch (claim.value_type) {
    case 'stream':
      if (claim.value) {
        if (!claim.value.source) {
          return 'livestream';
        } else {
          return claim.value.stream_type;
        }
      } else {
        return 'stream';
      }

    default:
      // collection, channel, repost, undefined
      return claim.value_type;
  }
}

export const doPublishDesktop = (filePath: undefined, preview?: boolean) => {
  return (dispatch: Dispatch, getState: () => State) => {
    const publishPreviewFn = (publishPayload, previewResponse) => {
      dispatch(
        doOpenModal(MODALS.PUBLISH_PREVIEW, {
          publishPayload,
          previewResponse,
        })
      );
    };

    const noFileParam = !filePath || filePath === NO_FILE;
    const state: State = getState();
    const editingUri = selectPublishFormValue(state, 'editingURI') || '';
    const remoteUrl = selectPublishFormValue(state, 'remoteFileUrl');
    const { memberRestrictionTierIds, name } = state.publish;
    const memberRestrictionStatus = selectMemberRestrictionStatus(state);
    const claim = makeSelectClaimForUri(editingUri)(state) || {};
    const hasSourceFile = claim.value && claim.value.source;
    const redirectToLivestream = noFileParam && !hasSourceFile && !remoteUrl;

    const publishSuccess = (successResponse, lbryFirstError) => {
      const state: State = getState();
      const myClaims = selectMyClaims(state);
      const pendingClaim = successResponse.outputs[0];

      const apiLogSuccessCb = (claimResult: ChannelClaim | StreamClaim) => {
        const channelClaimId = getChannelIdFromClaim(claimResult);

        // hit backend to save restricted memberships
        if (channelClaimId) {
          const tierIds = memberRestrictionStatus.isRestricting ? memberRestrictionTierIds : [];
          dispatch(
            doSaveMembershipRestrictionsForContent(channelClaimId, claimResult.claim_id, name, tierIds, undefined)
          );
        }
      };

      analytics.apiLog.publish(pendingClaim, apiLogSuccessCb);
      const { permanent_url: url } = pendingClaim;
      const actions: Array<any> = [];
      actions.push({
        type: ACTIONS.PUBLISH_SUCCESS,
        data: {
          type: resolveClaimTypeForAnalytics(pendingClaim),
        },
      });

      // We have to fake a temp claim until the new pending one is returned by claim_list_mine
      // We can't rely on claim_list_mine because there might be some delay before the new claims are returned
      // Doing this allows us to show the pending claim immediately, it will get overwritten by the real one
      const isMatch = (claim: any) => claim.claim_id === pendingClaim.claim_id;

      const isEdit = myClaims.some(isMatch);
      actions.push({
        type: ACTIONS.UPDATE_PENDING_CLAIMS,
        data: {
          claims: [pendingClaim],
          options: {
            overrideTags: true,
            overrideSigningChannel: true,
          },
        },
      } as UpdatePendingClaimsAction);
      dispatch(batchActions(...actions));
      dispatch(
        doOpenModal(MODALS.PUBLISH, {
          uri: url,
          isEdit,
          filePath,
          lbryFirstError,
        })
      );
      dispatch(doCheckPendingClaims(undefined));
      if (redirectToLivestream) {
        dispatch(doClearPublish());
        navigateTo(`/$/${PAGES.LIVESTREAM}`);
      }
    };

    const publishFail = (error: any) => {
      const actions: Array<any> = [];
      actions.push({
        type: ACTIONS.PUBLISH_FAIL,
      });
      let message = typeof error === 'string' ? error : error.message;

      if (message.endsWith(ERRORS.SDK_FETCH_TIMEOUT)) {
        message = ERRORS.PUBLISH_TIMEOUT_BUT_LIKELY_SUCCESSFUL;

        if (memberRestrictionStatus.isRestricting) {
          message = ERRORS.RESTRICTED_CONTENT_PUBLISHING_FAILED;
        }
      }

      actions.push(
        doError({
          message,
          cause: error.cause,
        })
      );
      dispatch(batchActions(...actions));
    };

    if (preview) {
      dispatch(doPublish(publishSuccess, publishFail, publishPreviewFn));
      return;
    }

    if (!redirectToLivestream) {
      navigateTo(`/$/${PAGES.UPLOADS}`);
    }

    dispatch(doPublish(publishSuccess, publishFail));
  };
};
export const doPublishResume = (publishPayload: FileUploadSdkParams) => (dispatch: Dispatch, getState: GetState) => {
  const publishSuccess = (successResponse, lbryFirstError) => {
    const state = getState();
    const myClaimIds = selectMyActiveClaims(state) as Set<string>;
    const pendingClaim = successResponse.outputs[0];
    const { permanent_url: url } = pendingClaim;
    const { memberRestrictionTierIds, name } = state.publish;
    const memberRestrictionStatus = selectMemberRestrictionStatus(state);

    const apiLogSuccessCb = (claimResult: ChannelClaim | StreamClaim) => {
      const channelClaimId = getChannelIdFromClaim(claimResult);

      // hit backend to save restricted memberships
      if (channelClaimId) {
        const tierIds = memberRestrictionStatus.isRestricting ? memberRestrictionTierIds : [];
        dispatch(
          doSaveMembershipRestrictionsForContent(channelClaimId, claimResult.claim_id, name, tierIds, undefined)
        );
      }
    };

    analytics.apiLog.publish(pendingClaim, apiLogSuccessCb);
    // We have to fake a temp claim until the new pending one is returned by claim_list_mine
    // We can't rely on claim_list_mine because there might be some delay before the new claims are returned
    // Doing this allows us to show the pending claim immediately, it will get overwritten by the real one
    const isEdit = myClaimIds.has(pendingClaim.claim_id);
    const actions: Array<any> = [];
    actions.push({
      type: ACTIONS.PUBLISH_SUCCESS,
      data: {
        type: resolveClaimTypeForAnalytics(pendingClaim),
      },
    });
    actions.push({
      type: ACTIONS.UPDATE_PENDING_CLAIMS,
      data: {
        claims: [pendingClaim],
      },
    });
    dispatch(batchActions(...actions));
    dispatch(
      doOpenModal(MODALS.PUBLISH, {
        uri: url,
        isEdit,
        lbryFirstError,
      })
    );
    dispatch(doCheckPendingClaims(undefined));
  };

  const publishFail = (error: any) => {
    const actions: Array<any> = [];
    actions.push({
      type: ACTIONS.PUBLISH_FAIL,
    });
    actions.push(
      doError({
        message: error.message,
        cause: error.cause,
      })
    );
    dispatch(batchActions(...actions));
  };

  dispatch(doPublish(publishSuccess, publishFail, undefined, publishPayload));
};
export const doResetThumbnailStatus = () => (dispatch: Dispatch) => {
  dispatch({
    type: ACTIONS.UPDATE_PUBLISH_FORM,
    data: {
      thumbnailPath: '',
      thumbnailError: undefined,
    },
  });
  return dispatch({
    type: ACTIONS.UPDATE_PUBLISH_FORM,
    data: {
      uploadThumbnailStatus: THUMBNAIL_STATUSES.READY,
      thumbnail: '',
    },
  });
};
export const doBeginPublish = (type: PublishType, name: string = '', customPath: string = '') => {
  return (dispatch: Dispatch, getState: GetState) => {
    assert(PUBLISH_PATH_MAP[type], 'invalid type', type);

    const state = getState();
    const currentType = state.publish.type;
    const currentFormId = state.publish.activeFormId;

    if (!(currentType === type && currentFormId)) {
      if (currentFormId) {
        dispatch({ type: 'PUBLISH_SAVE_FORM', data: { id: currentFormId } });
      }
      dispatch(doClearPublish());
      dispatch({ type: 'PUBLISH_SET_ACTIVE_FORM', data: { id: null } });
      dispatch({ type: ACTIONS.UPDATE_PUBLISH_FORM, data: { type } });
    }

    if (name) {
      dispatch({ type: ACTIONS.UPDATE_PUBLISH_FORM, data: { name } });
    }

    if (customPath) {
      navigateTo(customPath);
    } else {
      const path = PUBLISH_PATH_MAP[type] || PUBLISH_PATH_MAP.file;
      navigateTo(`/$/${path}`);
    }
  };
};
export const doClearPublish = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const defaultChannelClaim = selectDefaultChannelClaim(state);
  const channelLanguages = makeSelectMetadataItemForUri(defaultChannelClaim?.permanent_url, 'languages')(state);
  const channelPrimaryLanguage = channelLanguages ? channelLanguages[0] : null;
  dispatch({
    type: ACTIONS.CLEAR_PUBLISH,
    data: {
      language: channelPrimaryLanguage,
    },
  });
  return dispatch(doResetThumbnailStatus());
};
export const doSavePublishForm = (id: string) => (dispatch: Dispatch) =>
  dispatch({ type: ACTIONS.PUBLISH_SAVE_FORM, data: { id } });

export const doRestorePublishForm = (id: string) => (dispatch: Dispatch) =>
  dispatch({ type: ACTIONS.PUBLISH_RESTORE_FORM, data: { id } });

export const doSwitchPublishForm = (targetId: string, currentId?: string) => (dispatch: Dispatch) => {
  if (currentId) {
    dispatch({ type: ACTIONS.PUBLISH_SAVE_FORM, data: { id: currentId } });
  }
  dispatch({ type: ACTIONS.PUBLISH_RESTORE_FORM, data: { id: targetId } });
};

export const doUpdatePublishForm = (publishFormValue: UpdatePublishState) => (dispatch: Dispatch) =>
  dispatch({
    type: ACTIONS.UPDATE_PUBLISH_FORM,
    data: { ...publishFormValue },
  });
export const doUpdateTitle = (title: string, skipNameAutoFill: boolean) => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const { name, claimToEdit } = state.publish;
  const regexInvalidURI =
    /[ =&#:$@%?;/\\\n"<>%{}|^~[\]`\u{0000}-\u{0008}\u{000b}-\u{000c}\u{000e}-\u{001F}\u{D800}-\u{DFFF}\u{FFFE}-\u{FFFF}]/gu; // eslint-disable-line no-control-regex
  const publishFormValue = {
    name,
    title,
  };
  // Keep the name matching the title, if the name was already matching
  let newName = title.replace(regexInvalidURI, '-');

  if (
    !claimToEdit &&
    !skipNameAutoFill &&
    (newName.startsWith(name) || newName === name.slice(0, -1) || !title || !name)
  ) {
    publishFormValue.name = newName;
  }

  dispatch({
    type: ACTIONS.UPDATE_PUBLISH_FORM,
    data: { ...publishFormValue },
  });
};
/** Fallback metadata extraction via <video> element when MediaBunny is unavailable */
function extractMetadataViaVideoElement(blob: Blob, dispatch: Dispatch, shouldApplyMetadata: () => boolean) {
  window.URL = window.URL || window.webkitURL;
  const video = document.createElement('video');
  const objectUrl = window.URL.createObjectURL(blob);

  const cleanup = () => {
    window.URL.revokeObjectURL(objectUrl);
    video.removeAttribute('src');
    video.load();
  };

  video.preload = 'metadata';
  video.addEventListener('loadedmetadata', () => {
    if (!shouldApplyMetadata()) {
      cleanup();
      return;
    }

    dispatch({
      type: ACTIONS.UPDATE_PUBLISH_FORM,
      data: {
        fileDur: video.duration,
        fileBitrate: getVideoBitrate(blob.size, video.duration),
      },
    });
    cleanup();
  });
  video.addEventListener('error', () => {
    if (!shouldApplyMetadata()) {
      cleanup();
      return;
    }

    dispatch({
      type: ACTIONS.UPDATE_PUBLISH_FORM,
      data: { fileDur: 0, fileBitrate: 0 },
    });
    cleanup();
  });
  video.src = objectUrl;
}

export const doUpdateFile = (file: WebFile, clearName: boolean = true) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const { name, title } = state.publish;
    const isStillEditing = selectIsStillEditing(state);

    if (!file) {
      // This also handles the case of trying to select another file but canceling half way.
      if (isStillEditing || !clearName) {
        dispatch({
          type: ACTIONS.UPDATE_PUBLISH_FORM,
          data: {
            filePath: '',
            fileDur: 0,
            fileBitrate: 0,
            fileVideoCodec: '',
            fileAudioCodec: '',
            fileFormat: '',
            fileWidth: 0,
            fileHeight: 0,
            fileFps: 0,
            fileNeedsTransmux: false,
            fileText: undefined,
          },
        });
      } else {
        dispatch({
          type: ACTIONS.UPDATE_PUBLISH_FORM,
          data: {
            filePath: '',
            name: '',
            fileDur: 0,
            fileBitrate: 0,
            fileVideoCodec: '',
            fileAudioCodec: '',
            fileFormat: '',
            fileWidth: 0,
            fileHeight: 0,
            fileFps: 0,
            fileNeedsTransmux: false,
            fileText: undefined,
          },
        });
      }

      return;
    }

    assert(typeof file !== 'string');
    const selectedFilePath = file.path || file;
    const shouldApplyMetadata = () => selectPublishFormValue(getState(), 'filePath') === selectedFilePath;
    const contentType = file.type && file.type.split('/');
    const isVideo = contentType ? contentType[0] === 'video' : false;
    const isMp4 = contentType ? contentType[1] === 'mp4' : false;
    let isTextPost = false;

    if (contentType && contentType[0] === 'text') {
      isTextPost = contentType[1] === 'plain' || contentType[1] === 'markdown'; // setCurrentFileType(contentType);
    } else if (file.name) {
      // If user's machine is missing a valid content type registration
      // for markdown content: text/markdown, file extension will be used instead
      const extension = file.name.split('.').pop();
      const MARKDOWN_FILE_EXTENSIONS = ['txt', 'md', 'markdown'];
      isTextPost = MARKDOWN_FILE_EXTENSIONS.includes(extension);
    }

    const fileExtension = file.name?.toLowerCase().split('.').pop() || '';
    const isMp4Container = fileExtension === 'mp4' || fileExtension === 'mov' || fileExtension === 'm4v';

    const formUpdates: UpdatePublishState = {
      fileSize: file.size,
      fileMime: file.type,
      fileVid: isVideo,
      fileDur: 0,
      fileBitrate: 0,
      fileVideoCodec: '',
      fileAudioCodec: '',
      fileFormat: fileExtension,
      fileWidth: 0,
      fileHeight: 0,
      fileFps: 0,
      fileNeedsTransmux: isVideo && !isMp4Container,
      fileSizeTooBig: false,
      fileText: undefined,
    };

    // --- Async metadata extraction via MediaBunny ---
    const isMedia = isVideo || (contentType && contentType[0] === 'audio');
    if (isMedia) {
      import('odysee-media-usagi')
        .then(async (mb) => {
          let input;

          try {
            input = new mb.Input({
              formats: mb.ALL_FORMATS,
              source: new mb.BlobSource(file as unknown as Blob),
            });

            const duration = (await input.computeDuration()) || 0;
            const videoTrack = await input.getPrimaryVideoTrack();
            const audioTrack = await input.getPrimaryAudioTrack();

            const metadataUpdates: UpdatePublishState = {
              fileDur: duration,
              fileBitrate: duration > 0 ? (file.size * 8) / duration : 0,
            };

            if (videoTrack) {
              const packetStats = await videoTrack.computePacketStats(120);
              metadataUpdates.fileVideoCodec = videoTrack.codec || '';
              metadataUpdates.fileWidth = videoTrack.displayWidth || 0;
              metadataUpdates.fileHeight = videoTrack.displayHeight || 0;
              metadataUpdates.fileFps = packetStats.averagePacketRate || 0;
            }
            if (audioTrack) {
              metadataUpdates.fileAudioCodec = audioTrack.codec || '';
            }

            // Detect non-MP4 containers for transmux prompt
            metadataUpdates.fileFormat = isMp4Container ? 'mp4' : fileExtension;
            metadataUpdates.fileNeedsTransmux = isVideo && !isMp4Container;

            if (shouldApplyMetadata()) {
              dispatch({
                type: ACTIONS.UPDATE_PUBLISH_FORM,
                data: metadataUpdates,
              });
            }
          } catch (e) {
            console.warn('[Publish] MediaBunny metadata extraction failed, falling back:', e); // eslint-disable-line no-console
            // Fallback: try <video> element for MP4
            if (isMp4) {
              extractMetadataViaVideoElement(file as unknown as Blob, dispatch, shouldApplyMetadata);
            }
          } finally {
            if (input) {
              input.dispose();
            }
          }
        })
        .catch(() => {
          // MediaBunny not available — fallback to <video> element
          if (isMp4) {
            extractMetadataViaVideoElement(file as unknown as Blob, dispatch, shouldApplyMetadata);
          }
        });
    } else {
      formUpdates.fileDur = 0;

      if (isTextPost) {
        const reader = new FileReader();
        reader.addEventListener('load', (event: ProgressEvent) => {
          // See: https://github.com/facebook/flow/issues/3470
          if (event.target instanceof FileReader) {
            const text = event.target.result;
            dispatch({
              type: ACTIONS.UPDATE_PUBLISH_FORM,
              data: {
                fileText: text,
              },
            }); // setPublishMode(PUBLISH_MODES.POST);
          }
        });
        reader.readAsText(file as unknown as Blob); // setCurrentFileType('text/markdown');
      }
    }

    // --- File Size ---
    const TV_PUBLISH_SIZE_LIMIT_BYTES = WEB_PUBLISH_SIZE_LIMIT_GB * 1073741824;

    if (file.size && Number(file.size) > TV_PUBLISH_SIZE_LIMIT_BYTES) {
      formUpdates.fileSizeTooBig = true;
    }

    // --- Name and title ---
    // Strip off extension and replace invalid characters
    const newFileName = (file.name && file.name.substr(0, file.name.lastIndexOf('.'))) || '';
    const fileName = clearName ? newFileName : name || newFileName;

    if (clearName || !title) {
      formUpdates.title = newFileName || fileName;
    }

    if (!isStillEditing && clearName) {
      formUpdates.name = sanitizeName(newFileName || fileName);
    } else if (!isStillEditing && !name) {
      formUpdates.name = sanitizeName(fileName);
    }

    // --- File Path ---
    // If electron, we'll set filePath to the path string because SDK is handling publishing.
    // File.path will be undefined from web due to browser security, so it will default to the File Object.
    formUpdates.filePath = selectedFilePath;
    // --- Finalize ---
    dispatch({
      type: ACTIONS.UPDATE_PUBLISH_FORM,
      data: formUpdates,
    });
  };
};
export const doUploadThumbnail =
  (filePath?: string, thumbnailBlob?: File, fsAdapter?: any, fs?: any, path?: any, cb?: (arg0: string) => void) =>
  (dispatch: Dispatch) => {
    let thumbnail, fileExt, fileName, fileType, stats, size;

    const uploadError = (error: string | { message: string; cause: string } = '') => {
      dispatch(
        batchActions(
          {
            type: ACTIONS.UPDATE_PUBLISH_FORM,
            data: {
              uploadThumbnailStatus: THUMBNAIL_STATUSES.READY,
              thumbnail: '',
              nsfw: false,
            },
          },
          doError(error)
        )
      );
    };

    dispatch({
      type: ACTIONS.UPDATE_PUBLISH_FORM,
      data: {
        thumbnailError: undefined,
      },
    });

    const doUpload = (data) => {
      return fetch(IMG_CDN_PUBLISH_URL, {
        method: 'POST',
        body: data,
      })
        .then((res) => res.text())
        .then((text) => {
          try {
            return text.length ? JSON.parse(text) : {};
          } catch {
            throw new Error(text);
          }
        })
        .then((json) => {
          if (json.type !== 'success') {
            return uploadError(
              json.message || __('There was an error in the upload. The format or extension might not be supported.')
            );
          }

          if (cb) {
            cb(json.message);
          }

          return dispatch({
            type: ACTIONS.UPDATE_PUBLISH_FORM,
            data: {
              uploadThumbnailStatus: THUMBNAIL_STATUSES.COMPLETE,
              thumbnail: json.message,
            },
          });
        })
        .catch((err) => {
          let message = err.message;

          // This sucks but ¯\_(ツ)_/¯
          if (message === 'Failed to fetch') {
            // message = __('Thumbnail upload service may be down, try again later.');
            message = __(
              'Thumbnail upload service may be down, try again later. Some plugins like AdGuard Français may be blocking the service. If using Brave, go to brave://adblock and disable it, or turn down shields.'
            );
          }

          const userInput = [fileName, fileExt, fileType, thumbnail, size];
          uploadError({
            message,
            cause: userInput.join(' | '),
          });
        });
    };

    dispatch({
      type: ACTIONS.UPDATE_PUBLISH_FORM,
      data: {
        uploadThumbnailStatus: THUMBNAIL_STATUSES.IN_PROGRESS,
      },
    });

    if (fsAdapter && fsAdapter.readFile && filePath) {
      fsAdapter.readFile(filePath, 'base64').then((base64Image) => {
        fileExt = 'png';
        fileName = 'thumbnail.png';
        fileType = 'image/png';
        const data = new FormData();
        data.append('file-input', {
          uri: 'file://' + filePath,
          type: fileType,
          name: fileName,
        } as any);
        data.append('upload', 'Upload');
        return doUpload(data);
      });
    } else {
      if (filePath && fs && path) {
        thumbnail = fs.readFileSync(filePath);
        fileExt = path.extname(filePath);
        fileName = path.basename(filePath);
        stats = fs.statSync(filePath);
        size = stats.size;
        fileType = `image/${fileExt.slice(1)}`;
      } else if (thumbnailBlob) {
        fileExt = `.${thumbnailBlob.type && thumbnailBlob.type.split('/')[1]}`;
        fileName = thumbnailBlob.name;
        fileType = thumbnailBlob.type;
        size = thumbnailBlob.size;
      } else {
        return null;
      }

      if (size && size >= THUMBNAIL_CDN_SIZE_LIMIT_BYTES) {
        const maxSizeMB = THUMBNAIL_CDN_SIZE_LIMIT_BYTES / (1024 * 1024);
        uploadError(
          __('Thumbnail size over %max_size%MB, please edit and reupload.', {
            max_size: maxSizeMB,
          })
        );
        return;
      }

      const data = new FormData();
      const file =
        thumbnailBlob ||
        (thumbnail &&
          new File([thumbnail], fileName, {
            type: fileType,
          }));
      data.append('file-input', file);
      data.append('upload', 'Upload');
      return doUpload(data);
    }
  };
export const doPrepareEdit = (claim: StreamClaim, uri: string, claimType: string) => {
  assert(claim, 'doPrepareEdit: claim must not be null', {
    uri,
    claimType,
  });
  return async (dispatch: Dispatch, getState: GetState) => {
    const { name, amount, value = {} } = claim;
    const channelName = (claim && claim.signing_channel && claim.signing_channel.name) || null;
    const channelId = (claim && claim.signing_channel && claim.signing_channel.claim_id) || null;
    const {
      author,
      description,
      // use same values as default state
      // fee will be undefined for free content
      fee = {
        amount: '0',
        currency: 'LBC',
      },
      languages,
      license,
      license_url: licenseUrl,
      thumbnail,
      title,
      tags,
      stream_type,
    } = value;
    let state = getState();
    const isPostClaim = makeSelectFileRenderModeForUri(claim?.permanent_url)(state) === RENDER_MODES.MARKDOWN;
    const isLivestreamClaim = isStreamPlaceholderClaim(claim);
    const type: PublishType = isLivestreamClaim
      ? PUBLISH_TYPES.LIVESTREAM
      : isPostClaim
        ? PUBLISH_TYPES.POST
        : PUBLISH_TYPES.FILE;
    const liveCreateType = isLivestreamClaim ? 'edit_placeholder' : undefined;
    const liveEditType = isLivestreamClaim ? 'update_only' : undefined; // Reverted #2801

    const publishData: UpdatePublishState = {
      type,
      ...(liveCreateType
        ? {
            liveCreateType,
          }
        : {}),
      ...(liveEditType
        ? {
            liveEditType,
          }
        : {}),
      claim_id: claim.claim_id,
      name,
      bid: Number(amount),
      author,
      description,
      fee: { amount: Number(fee.amount), currency: fee.currency },
      languages,
      thumbnail: thumbnail ? thumbnail.url : null,
      title,
      uri,
      uploadThumbnailStatus: thumbnail ? THUMBNAIL_STATUSES.MANUAL : undefined,
      licenseUrl,
      nsfw: isClaimNsfw(claim),
      tags: tags
        ? tags.map((tag) => ({
            name: tag,
          }))
        : [],
      streamType: stream_type,
      claimToEdit: { ...claim },
    };

    // Make sure custom licenses are mapped properly
    // If the license isn't one of the standard licenses, map the custom license and description/url
    if (!CC_LICENSES.some(({ value }) => value === license)) {
      if (!license || license === NONE || license === PUBLIC_DOMAIN) {
        publishData.licenseType = license;
      } else if (license && !licenseUrl && license !== NONE) {
        publishData.licenseType = COPYRIGHT;
      } else {
        publishData.licenseType = OTHER;
      }

      publishData.otherLicenseDescription = license;
    } else {
      publishData.licenseType = license;
    }

    if (channelName) {
      publishData['channel'] = channelName;
    }

    if (channelId) {
      publishData.channelId = channelId;
    }

    // Fill purchase/rental details from the claim
    const rental = parseRentalTag(tags);
    const purchasePrice = parsePurchaseTag(tags);

    if (rental || purchasePrice) {
      publishData['paywall'] = PAYWALL.FIAT;
    } else if (fee.amount && Number(fee.amount) > 0) {
      publishData['paywall'] = PAYWALL.SDK;
    } else {
      publishData['paywall'] = PAYWALL.FREE;
    }

    if (rental) {
      publishData.fiatRentalEnabled = true;
      publishData.fiatRentalFee = {
        amount: rental.price,
        currency: 'USD', // TODO: hardcode until we have a direction on currency
      };
      publishData.fiatRentalExpiration = {
        // Don't know which unit the user picked since we store it as 'seconds'
        // in the tag. Just convert back to days for now.
        value: rental.expirationTimeInSeconds / TO_SECONDS['days'],
        unit: 'days',
      };
    }

    if (purchasePrice) {
      publishData.fiatPurchaseEnabled = true;
      publishData.fiatPurchaseFee = {
        amount: purchasePrice,
        currency: 'USD', // TODO: hardcode until we have a direction on currency
      };
    }

    // == Tag derivations ==
    const publishDataTags = new Set(publishData.tags && publishData.tags.map((tag) => tag.name));

    // -- Membership restrictions
    if (publishDataTags.has(MEMBERS_ONLY_CONTENT_TAG)) {
      if (channelId) {
        // Repopulate membership restriction IDs
        let protectedMembershipIds: Array<number> | void | null = selectProtectedContentMembershipsForContentClaimId(
          state,
          claim.claim_id
        );

        if (protectedMembershipIds === undefined) {
          await dispatch(doMembershipContentforStreamClaimId(claim.claim_id));
          state = getState();
          protectedMembershipIds = selectProtectedContentMembershipsForContentClaimId(state, claim.claim_id);
        }

        if (protectedMembershipIds && protectedMembershipIds.length > 0) {
          publishData.memberRestrictionOn = true;
          publishData.memberRestrictionTierIds = protectedMembershipIds;
        } else {
          publishData.memberRestrictionOn = false;
          publishData.memberRestrictionTierIds = [];
        }
      } else {
        // ??
        if (publishData.tags) {
          publishData.tags = publishData.tags.filter((tag) => tag.name === MEMBERS_ONLY_CONTENT_TAG);
        } else {
          publishData.tags = [];
        }
      }
    }

    // -- Visibility restrictions
    if (tags) {
      if (tags.includes(VISIBILITY_TAGS.UNLISTED)) {
        publishData.visibility = 'unlisted';
      } else if (tags.includes(VISIBILITY_TAGS.PRIVATE)) {
        publishData.visibility = 'private';
      } else if (tags.includes(SCHEDULED_TAGS.HIDE)) {
        publishData.visibility = 'scheduled';
        publishData.scheduledShow = false;
      } else if (tags.includes(SCHEDULED_TAGS.SHOW)) {
        publishData.visibility = 'scheduled';
        publishData.scheduledShow = true;
      } else {
        publishData.visibility = 'public';
      }
    } else {
      publishData.visibility = 'public';
    }

    if (publishData.channelId) {
      dispatch(doSetActiveChannel(publishData.channelId, false));
      dispatch(doSetIncognito(false));
    } else {
      dispatch(doSetIncognito(true));
    }

    dispatch({
      type: ACTIONS.DO_PREPARE_EDIT,
      data: publishData,
    });
    navigateTo(`/$/${PUBLISH_PATH_MAP[type]}`);
  };
};
// ---------------------------------------------------------------------------
// Copy from Previous Upload — search helper
// ---------------------------------------------------------------------------
const RECENT_PAGE_SIZE = 100;
const SEARCH_PAGE_SIZE_PER_CHANNEL = 24;

function sortClaimsByNewest(claims: Array<StreamClaim>): Array<StreamClaim> {
  return [...claims].toSorted((a, b) => {
    const aTime = Number(a?.value?.release_time || a?.meta?.creation_timestamp || 0);
    const bTime = Number(b?.value?.release_time || b?.meta?.creation_timestamp || 0);
    return bTime - aTime;
  });
}

function extractStreamClaims(result: any): Array<StreamClaim> {
  const items = (result && result.items) || [];
  return items.filter((item) => item && item.value_type === 'stream');
}

function clientTitleFilter(claims: Array<StreamClaim>, term: string): Array<StreamClaim> {
  const lower = term.toLowerCase();
  return claims.filter((c) => (c?.value?.title || c?.name || '').toLowerCase().includes(lower));
}

function getLighthouseClaimId(item: any): string | null | undefined {
  if (!item || typeof item !== 'object') return null;
  const rawClaimId = item.claimId || item.claim_id || item.claimID || item.claimid || item.id;
  if (!rawClaimId) return null;
  const claimId = String(rawClaimId).trim();
  return claimId || null;
}

async function hydrateClaimsInStore(dispatch: Dispatch, claims: Array<StreamClaim>): Promise<Array<StreamClaim>> {
  if (!Array.isArray(claims) || claims.length === 0) return [];
  const claimIds = [...new Set(claims.map((claim) => claim?.claim_id).filter(Boolean))];
  if (claimIds.length === 0) return claims;

  try {
    const resolveInfo = await dispatch(doResolveClaimIds(claimIds, true));
    if (!resolveInfo || typeof resolveInfo !== 'object') return claims;
    const resolvedById: Record<string, StreamClaim> = {};
    const resolveValues: Array<any> = Object.values(resolveInfo);
    resolveValues.forEach((info) => {
      const stream: StreamClaim | null | undefined = info && typeof info === 'object' ? info.stream : null;

      if (stream && stream.claim_id && stream.value_type === 'stream') {
        resolvedById[stream.claim_id] = stream;
      }
    });
    return claims.map((claim) => resolvedById[claim.claim_id] || claim);
  } catch {
    // Keep the existing result set if hydration fails; modal search should not hard-fail here.
    return claims;
  }
}

/**
 * Searches the current user's uploads.
 *
 * @param searchTerm - Text query
 * @param filter - Optional visibility filter: 'all' (default) or 'unlisted'
 *
 * For 'all': short queries use claim_list; 3+ char queries use Lighthouse.
 * For 'unlisted': uses claim_search with any_tags for the unlisted tag,
 *   since Lighthouse does not index unlisted content.
 */
export const doSearchMyUploads = (searchTerm: string = '', filter: string = 'all') => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const term = searchTerm.trim();
    const state = getState();
    const showMature = selectShowMatureContent(state);
    const myChannelIds = selectMyChannelClaimIds(state) || [];

    // ── Unlisted filter: use claim_search with any_tags ──
    if (filter === 'unlisted') {
      const csParams: any = {
        page: 1,
        page_size: RECENT_PAGE_SIZE,
        any_tags: [VISIBILITY_TAGS.UNLISTED],
        claim_type: ['stream'],
        order_by: ['release_time'],
        remove_duplicates: true,
      };

      if (myChannelIds.length > 0) {
        csParams.channel_ids = myChannelIds;
      }

      const result = await Lbry.claim_search(csParams);
      const claims = await hydrateClaimsInStore(dispatch, sortClaimsByNewest(extractStreamClaims(result)));
      return {
        claims: term.length > 0 ? clientTitleFilter(claims, term) : claims,
      };
    }

    // ── Scheduled filter: use claim_search with scheduled tags ──
    if (filter === 'scheduled') {
      const csParams: any = {
        page: 1,
        page_size: RECENT_PAGE_SIZE,
        any_tags: Object.values(SCHEDULED_TAGS),
        claim_type: ['stream'],
        order_by: ['release_time'],
        remove_duplicates: true,
      };

      if (myChannelIds.length > 0) {
        csParams.channel_ids = myChannelIds;
      }

      const result = await Lbry.claim_search(csParams);
      const claims = await hydrateClaimsInStore(dispatch, sortClaimsByNewest(extractStreamClaims(result)));
      return {
        claims: term.length > 0 ? clientTitleFilter(claims, term) : claims,
      };
    }

    // ── All uploads: short / empty query → claim_list ──
    if (term.length < 3) {
      const result = await Lbry.claim_list({
        page: 1,
        page_size: RECENT_PAGE_SIZE,
        resolve: true,
        claim_type: ['stream'],
      });
      const claims = await hydrateClaimsInStore(dispatch, sortClaimsByNewest(extractStreamClaims(result)));
      return {
        claims,
      };
    }

    // ── All uploads: no channels → title-filtered recent ──
    if (myChannelIds.length === 0) {
      const result = await Lbry.claim_list({
        page: 1,
        page_size: RECENT_PAGE_SIZE,
        resolve: true,
        claim_type: ['stream'],
      });
      const claims = clientTitleFilter(sortClaimsByNewest(extractStreamClaims(result)), term);
      const hydratedClaims = await hydrateClaimsInStore(dispatch, claims);
      return {
        claims: hydratedClaims,
      };
    }

    // ── All uploads: Lighthouse search across user's channels ──
    const channelIdsCsv = myChannelIds.join(',');
    const queryBase = `from=0&s=${encodeURIComponent(term)}&sort_by=release_time&nsfw=${
      showMature ? 'true' : 'false'
    }&size=${SEARCH_PAGE_SIZE_PER_CHANNEL}`;
    const response = await lighthouse.search(`${queryBase}&channel_id=${encodeURIComponent(channelIdsCsv)}`);
    const claimIds = [];

    if (Array.isArray(response?.body)) {
      response.body.forEach((item) => {
        const claimId = getLighthouseClaimId(item);
        if (claimId) claimIds.push(claimId);
      });
    }

    const uniqueClaimIds = [...new Set(claimIds)];
    const resolveInfo = uniqueClaimIds.length > 0 ? await dispatch(doResolveClaimIds(uniqueClaimIds, true)) : {};
    const resolvedClaims: Array<StreamClaim> = [];

    if (resolveInfo && typeof resolveInfo === 'object') {
      const resolveValues: Array<any> = Object.values(resolveInfo);
      resolveValues.forEach((info) => {
        const stream: StreamClaim | null | undefined = info && typeof info === 'object' ? info.stream : null;
        if (stream && stream.claim_id && stream.value_type === 'stream') resolvedClaims.push(stream);
      });
    }

    return {
      claims: sortClaimsByNewest(resolvedClaims),
    };
  };
};

/**
 * Populates the publish form with metadata from a previously published claim.
 * Unlike doPrepareEdit, this does NOT set editingURI/claimToEdit/name/claim_id,
 * so the form stays in "new upload" mode.
 *
 * @param claim - The source claim to copy from
 * @param fields - Array of field group keys to copy (e.g. ['title', 'description', 'tags', ...])
 */
export const doPopulatePublishFormFromClaim = (claim: StreamClaim, fields: Array<string>) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    if (!claim) return;
    const { value = {} } = claim;
    const {
      description,
      fee = {
        amount: 0,
        currency: 'LBC',
      },
      languages,
      license,
      license_url: licenseUrl,
      thumbnail,
      title,
      tags: claimTags,
    } = value;
    let state = getState();
    const publishData: UpdatePublishState = {};
    const fieldSet = new Set(fields);
    const tags = claimTags || [];
    const normalizedFee = {
      amount: Number(fee.amount) || 0,
      currency: fee.currency || 'LBC',
    };

    // -- Title
    if (fieldSet.has('title')) {
      publishData.title = title || '';
    }

    // -- Description
    if (fieldSet.has('description')) {
      publishData.description = description || '';
    }

    // -- Tags (filter out internal/system tags)
    if (fieldSet.has('tags')) {
      const filteredTags = tags.filter(
        (tag) =>
          tag !== VISIBILITY_TAGS.UNLISTED &&
          tag !== VISIBILITY_TAGS.PRIVATE &&
          tag !== SCHEDULED_TAGS.HIDE &&
          tag !== SCHEDULED_TAGS.SHOW &&
          tag !== MEMBERS_ONLY_CONTENT_TAG
      );
      publishData.tags = filteredTags.map((tag) => ({
        name: tag,
      }));
      // Keep explicit to avoid stale NSFW state from previous form values.
      publishData.nsfw = isClaimNsfw(claim);
    }

    // -- Thumbnail
    if (fieldSet.has('thumbnail')) {
      const thumbnailUrl = thumbnail?.url || '';
      publishData.thumbnail = thumbnailUrl;
      publishData.uploadThumbnailStatus = thumbnailUrl ? THUMBNAIL_STATUSES.MANUAL : THUMBNAIL_STATUSES.READY;
    }

    // -- Language
    if (fieldSet.has('languages')) {
      const normalizedLanguages = languages || [];
      publishData.languages = normalizedLanguages;
      publishData.language = normalizedLanguages[0] || '';
    }

    // -- License
    if (fieldSet.has('license')) {
      publishData.licenseType = NONE;
      publishData.licenseUrl = '';
      publishData.otherLicenseDescription = '';

      if (license) {
        const isStandardLicense = CC_LICENSES.some(({ value }) => value === license);

        if (isStandardLicense) {
          publishData.licenseType = license;
        } else if (license === NONE || license === PUBLIC_DOMAIN) {
          publishData.licenseType = license;
        } else if (!licenseUrl) {
          publishData.licenseType = COPYRIGHT;
          publishData.otherLicenseDescription = license;
        } else {
          publishData.licenseType = OTHER;
          publishData.otherLicenseDescription = license;
        }

        if (licenseUrl) {
          publishData.licenseUrl = licenseUrl;
        }
      }
    }

    // -- Visibility
    if (fieldSet.has('visibility')) {
      if (tags.includes(VISIBILITY_TAGS.UNLISTED)) {
        publishData.visibility = 'unlisted';
      } else if (tags.includes(VISIBILITY_TAGS.PRIVATE)) {
        publishData.visibility = 'private';
      } else {
        publishData.visibility = 'public';
      }

      // Don't copy scheduled visibility; scheduling is per-upload.
      // Reset first to avoid stale restrictions from previous form values.
      publishData.memberRestrictionOn = false;
      publishData.memberRestrictionTierIds = [];
    }

    // -- Price / Paywall
    if (fieldSet.has('price')) {
      publishData.paywall = PAYWALL.FREE;
      publishData.fiatPurchaseEnabled = false;
      publishData.fiatRentalEnabled = false;
      const rental = parseRentalTag(tags);
      const purchasePrice = parsePurchaseTag(tags);

      if (rental || purchasePrice) {
        publishData.paywall = PAYWALL.FIAT;
      } else if (normalizedFee.amount > 0) {
        publishData.paywall = PAYWALL.SDK;
        publishData.fee = normalizedFee;
      } else {
        publishData.paywall = PAYWALL.FREE;
      }

      if (rental) {
        publishData.fiatRentalEnabled = true;
        publishData.fiatRentalFee = {
          amount: rental.price,
          currency: 'USD',
        };
        publishData.fiatRentalExpiration = {
          value: rental.expirationTimeInSeconds / TO_SECONDS['days'],
          unit: 'days',
        };
      }

      if (purchasePrice) {
        publishData.fiatPurchaseEnabled = true;
        publishData.fiatPurchaseFee = {
          amount: purchasePrice,
          currency: 'USD',
        };
      }
    }

    // -- Membership restrictions
    if (fieldSet.has('visibility') && tags.includes(MEMBERS_ONLY_CONTENT_TAG)) {
      const sourceChannelId = claim.signing_channel?.claim_id || null;
      const targetChannelId = state.publish.channelId;

      // Membership IDs are channel-specific. Copy only when source/destination channels match.
      if (sourceChannelId && sourceChannelId === targetChannelId) {
        let protectedMembershipIds = selectProtectedContentMembershipsForContentClaimId(state, claim.claim_id);

        if (protectedMembershipIds === undefined) {
          await dispatch(doMembershipContentforStreamClaimId(claim.claim_id));
          state = getState();
          protectedMembershipIds = selectProtectedContentMembershipsForContentClaimId(state, claim.claim_id);
        }

        if (protectedMembershipIds && protectedMembershipIds.length > 0) {
          publishData.memberRestrictionOn = true;
          publishData.memberRestrictionTierIds = protectedMembershipIds;
        }
      }
    }

    dispatch(doUpdatePublishForm(publishData));
  };
};
export const doPublish =
  (
    success: (...args: Array<any>) => any,
    fail: (...args: Array<any>) => any,
    previewFn?: (...args: Array<any>) => any,
    payload?: FileUploadSdkParams
  ) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (!previewFn) {
      dispatch({
        type: ACTIONS.PUBLISH_START,
      });
    }

    const state = getState();
    const myClaimForUri = state.publish.claimToEdit;
    const myChannels = selectMyChannelClaims(state) as Array<ChannelClaim> | null | undefined;
    // const myClaims = selectMyClaimsWithoutChannels(state);
    // get redux publish form
    const publishData = selectPublishFormValues(state);
    const { memberRestrictionTierIds, name } = publishData;
    const memberRestrictionStatus = selectMemberRestrictionStatus(state);
    const publishPayload =
      payload ||
      resolvePublishPayload(publishData, myClaimForUri, myChannels, memberRestrictionStatus, Boolean(previewFn));
    const { channel_id: channelClaimId } = publishPayload;
    const existingClaimId = myClaimForUri?.claim_id || '';

    // hit backend to save restricted memberships
    // hit the backend immediately to save the data, we will overwrite it if publish succeeds
    if (channelClaimId && !previewFn) {
      dispatch(
        doSaveMembershipRestrictionsForContent(
          channelClaimId,
          existingClaimId,
          name,
          memberRestrictionStatus.isRestricting ? memberRestrictionTierIds : [],
          existingClaimId ? undefined : true
        )
      );
    }

    if (previewFn) {
      const ESTIMATE_PUBLISH_COST = false;

      if (ESTIMATE_PUBLISH_COST) {
        const payloadSnapshot = { ...publishPayload }; // Lbry alters the payload, so make copy for previewFn

        return Lbry.publish(publishPayload).then((previewResponse: PublishResponse) => {
          return previewFn(payloadSnapshot, previewResponse);
        }, fail);
      } else {
        return previewFn(publishPayload, null);
      }
    }

    return Lbry.publish(publishPayload).then((response: PublishResponse) => {
      // TODO: Restore LbryFirst
      // if (!useLBRYUploader) {
      return success(response); // }
      // publishPayload.permanent_url = response.outputs[0].permanent_url;
      //
      // return LbryFirst.upload(publishPayload)
      //   .then(() => {
      //     // Return original publish response so app treats it like a normal publish
      //     return success(response);
      //   })
      //   .catch((error) => {
      //     return success(response, error);
      //   });
    }, fail);
  };
export const doPublishWithEarlyUpload =
  (tusUrlPromise: Promise<{ tusUrl: string }>, guid: string) => async (dispatch: Dispatch, getState: GetState) => {
    dispatch({ type: ACTIONS.PUBLISH_START });
    navigateTo(`/$/${PAGES.UPLOADS}`);

    try {
      const { tusUrl } = await tusUrlPromise;

      const state = getState();
      const myClaimForUri = state.publish.claimToEdit;
      const myChannels = selectMyChannelClaims(state) as Array<ChannelClaim> | null | undefined;
      const publishData = selectPublishFormValues(state);
      const { memberRestrictionTierIds, name } = publishData;
      const memberRestrictionStatus = selectMemberRestrictionStatus(state);
      const publishPayload = resolvePublishPayload(
        publishData,
        myClaimForUri,
        myChannels,
        memberRestrictionStatus,
        false
      );
      const { channel_id: channelClaimId } = publishPayload;
      const existingClaimId = myClaimForUri?.claim_id || '';

      if (channelClaimId) {
        dispatch(
          doSaveMembershipRestrictionsForContent(
            channelClaimId,
            existingClaimId,
            name,
            memberRestrictionStatus.isRestricting ? memberRestrictionTierIds : [],
            existingClaimId ? undefined : true
          )
        );
      }

      const { createClaim } = await import('web/setup/publish-v4-tasks');
      const { pollPublishStatus } = await import('web/setup/publish-v4');

      const token =
        Lbry.getApiRequestHeaders() && Object.keys(Lbry.getApiRequestHeaders()).includes(X_LBRY_AUTH_TOKEN)
          ? Lbry.getApiRequestHeaders()[X_LBRY_AUTH_TOKEN]
          : '';

      const { uploadUrl, guid: _guid, remote_url, publishId: _pid, ...sdkParams } = publishPayload;

      const publishId = await createClaim(token, tusUrl, sdkParams, {
        onSuccess: () => {},
        onFailure: () => {},
      });

      const publishedUri = buildURI(
        { streamName: publishData.name, channelName: publishData.channel } as LbryUrlObj,
        true
      );
      const signingChannel = myChannels?.find((ch: any) => ch.claim_id === channelClaimId);
      const pendingClaimId = `pending-${guid}`;
      const fakePendingClaim: any = {
        claim_id: pendingClaimId,
        name: publishData.name,
        permanent_url: publishedUri,
        canonical_url: publishedUri,
        short_url: publishedUri,
        type: 'claim',
        value_type: 'stream',
        confirmations: 0,
        is_channel_signature_valid: !!signingChannel,
        value: {
          title: publishData.title,
          description: publishData.description,
          thumbnail: { url: publishData.thumbnail },
          tags: publishData.tags?.map((t: any) => t.name) || [],
          source: { media_type: 'video/mp4' },
        },
        signing_channel: signingChannel
          ? {
              claim_id: signingChannel.claim_id,
              name: signingChannel.name,
              permanent_url: signingChannel.permanent_url,
              canonical_url: signingChannel.canonical_url,
              value: signingChannel.value,
            }
          : undefined,
        txid: pendingClaimId,
        nout: 0,
        meta: { effective_amount: '0' },
        timestamp: Math.floor(Date.now() / 1000),
      };

      dispatch({
        type: ACTIONS.UPDATE_PENDING_CLAIMS,
        data: { claims: [fakePendingClaim], options: { overrideTags: true, overrideSigningChannel: true } },
      } as UpdatePendingClaimsAction);
      dispatch({
        type: ACTIONS.PUBLISH_PIPELINE_UPDATE,
        data: { id: guid, updates: { stage: 'published', progress: 100, uri: publishedUri } },
      });
      dispatch({ type: ACTIONS.PUBLISH_SET_ACTIVE_FORM, data: { id: null } });
      dispatch({ type: ACTIONS.PUBLISH_SUCCESS, data: {} });

      pollPublishStatus(token, publishId, guid, dispatch)
        .then((sdkResult) => {
          const pendingClaim = sdkResult.outputs[0];
          dispatch({ type: ACTIONS.REMOVE_PENDING_CLAIM_BY_ID, data: { claimId: pendingClaimId } });
          dispatch({
            type: ACTIONS.UPDATE_PENDING_CLAIMS,
            data: { claims: [pendingClaim], options: { overrideTags: true, overrideSigningChannel: true } },
          } as UpdatePendingClaimsAction);
          dispatch(doCheckPendingClaims(undefined));
        })
        .catch(() => {});
    } catch (error: any) {
      dispatch({ type: ACTIONS.PUBLISH_FAIL });
      dispatch({
        type: ACTIONS.PUBLISH_PIPELINE_UPDATE,
        data: { id: guid, updates: { stage: 'error', error: error?.message } },
      });
      dispatch(doError({ message: typeof error === 'string' ? error : error.message, cause: error.cause }));
    }
  };

// Calls file_list until any reflecting files are done
export const doCheckReflectingFiles = () => (dispatch: Dispatch, getState: GetState) => {
  const state = getState();
  const { checkingReflector } = state.claims;
  let reflectorCheckInterval;

  const checkFileList = async () => {
    const state = getState();
    const reflectingById = selectReflectingById(state);
    const ids = Object.keys(reflectingById);
    const newReflectingById = {};
    const promises = [];

    // TODO: just use file_list({claim_id: Array<claimId>})
    if (Object.keys(reflectingById).length) {
      ids.forEach((claimId) => {
        promises.push(
          Lbry.file_list({
            claim_id: claimId,
          })
        );
      });
      Promise.all(promises)
        .then((results) => {
          results.forEach((res) => {
            if (res.items[0]) {
              const fileListItem = res.items[0];
              const fileClaimId = fileListItem.claim_id;
              const {
                is_fully_reflected: done,
                uploading_to_reflector: uploading,
                reflector_progress: progress,
              } = fileListItem;

              if (uploading) {
                newReflectingById[fileClaimId] = {
                  fileListItem: fileListItem,
                  progress,
                  stalled: !done && !uploading,
                };
              }
            }
          });
        })
        .then(() => {
          dispatch({
            type: ACTIONS.UPDATE_FILES_REFLECTING,
            data: newReflectingById,
          });

          if (!Object.keys(newReflectingById).length) {
            dispatch({
              type: ACTIONS.TOGGLE_CHECKING_REFLECTING,
              data: false,
            });
            clearInterval(reflectorCheckInterval);
          }
        });
    } else {
      dispatch({
        type: ACTIONS.TOGGLE_CHECKING_REFLECTING,
        data: false,
      });
      clearInterval(reflectorCheckInterval);
    }
  };

  // do it once...
  checkFileList();

  // then start the interval if it's not already started
  if (!checkingReflector) {
    dispatch({
      type: ACTIONS.TOGGLE_CHECKING_REFLECTING,
      data: true,
    });
    reflectorCheckInterval = setInterval(() => {
      checkFileList();
    }, 5000);
  }
};
export function doUpdateUploadAdd(
  file: File | string,
  params: Record<string, any>,
  uploader: TusUploader | XMLHttpRequest,
  backend: UploadBackendVersion
) {
  return (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_ADD,
      data: {
        file,
        params,
        uploader,
        backend,
      },
    });
  };
}
export const doUpdateUploadProgress =
  (props: { guid: string; progress?: string; status?: UploadStatus; publishId?: string | number }) =>
  (dispatch: Dispatch) =>
    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_PROGRESS,
      data: props,
    });

/**
 * doUpdateUploadRemove
 *
 * @param guid
 * @param params Optional. Retain to allow removal of old keys, which are
 *               derived from `name#channel` instead of using a guid.
 *               Can be removed after January 2022.
 * @returns {(function(Dispatch, GetState): void)|*}
 */
export function doUpdateUploadRemove(guid: string, params?: Record<string, any>) {
  return (dispatch: Dispatch, getState: GetState) => {
    // Clean up any cached optimized/converted files from IndexedDB
    const state = getState();
    const upload = state.publish.currentUploads?.[guid];
    if (upload?.file) {
      const f = upload.file;
      const fileName = typeof f === 'string' ? f : f.name;
      const fileSize = typeof f === 'object' && 'size' in f ? f.size : 0;
      import('util/uploadCache')
        .then(({ removeCachedFile }) => {
          removeCachedFile(`optimized-${fileName}-${fileSize}`).catch(() => {});
          removeCachedFile(`converted-${fileName}-${fileSize}`).catch(() => {});
        })
        .catch(() => {});
    }

    dispatch({
      type: ACTIONS.UPDATE_UPLOAD_REMOVE,
      data: {
        guid,
        params,
      },
    });
  };
}
// -- Flow exports --
export type DoPublishDesktop = typeof doPublishDesktop;
export type DoUpdateUploadRemove = typeof doUpdateUploadRemove;
